<template>
    <v-container fluid class="py-8">
        <!-- Header -->
        <v-row class="mb-4" justify="space-between">
            <v-col cols="auto">
                <span class="text-overline text-medium-emphasis d-block mb-1">Current Agents</span>
                <h1 class="text-h5 font-weight-bold">View and Manage Agents</h1>
            </v-col>
            <v-col cols="auto"></v-col>
        </v-row>

        <!-- Filters -->
        <v-card elevation="2" class="mb-6">
            <v-card-text class="py-6">
                <div class="filters-wrap mx-auto">
                    <v-row align="center" justify="center" class="ga-3 equal-bar">
                        <v-col cols="12" md="7">
                            <v-text-field v-model="search" label="Search (name, email, phone)" variant="outlined"
                                density="comfortable" hide-details="auto" clearable @keyup.enter="applyFilters" />
                        </v-col>

                        <v-col cols="12" md="2" class="d-flex">
                            <v-btn block class="w-100" variant="outlined" color="grey" :disabled="!search"
                                @click="clearFilters">
                                Clear
                            </v-btn>
                        </v-col>

                        <v-col cols="12" md="2" class="d-flex">
                            <v-btn block class="w-100" color="primary" @click="applyFilters">
                                Search
                            </v-btn>
                        </v-col>
                    </v-row>
                </div>
            </v-card-text>
        </v-card>


        <!-- Loading skeleton -->
        <v-card v-if="isLoading" elevation="2" class="mb-6">
            <v-card-title>Agents</v-card-title>
            <v-card-text>
                <div class="d-flex mb-2">
                    <v-skeleton-loader type="text" width="24%" class="mr-4" />
                    <v-skeleton-loader type="text" width="24%" class="mr-4" />
                    <v-skeleton-loader type="text" width="18%" class="mr-4" />
                    <v-skeleton-loader type="text" width="10%" class="mr-4" />
                    <v-skeleton-loader type="text" width="10%" class="mr-4" />
                    <v-skeleton-loader type="text" width="8%" class="ml-auto" />
                </div>
                <div v-for="n in pageSize" :key="n" class="d-flex align-center py-3">
                    <v-skeleton-loader type="avatar" class="mr-3" />
                    <v-skeleton-loader type="text" width="20%" class="mr-4" />
                    <v-skeleton-loader type="text" width="24%" class="mr-4" />
                    <v-skeleton-loader type="text" width="18%" class="mr-4" />
                    <v-skeleton-loader type="text" width="10%" class="mr-4" />
                    <v-skeleton-loader type="text" width="10%" class="mr-4" />
                    <v-skeleton-loader type="button" width="100" class="ml-auto" />
                </div>
            </v-card-text>
        </v-card>

        <!-- Table -->
        <v-card v-else elevation="2" class="mb-6">
            <v-card-title>Agents</v-card-title>
            <v-card-text>
                <v-table>
                    <thead>
                        <tr>
                            <th class="text-left">Agent Name</th>
                            <th class="text-left">Agency Name</th>
                            <th class="text-left">Email</th>
                            <th class="text-left">Phone</th>
                            <th class="text-left">ITA Number</th>
                            <th class="text-left">Paid</th>
                            <th class="text-left">Created</th>
                            <th class="text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="u in agents" :key="u.uid">
                            <!-- Agent Name -->
                            <td>
                                <div class="d-flex align-center">
                                    <v-avatar size="36" class="mr-3">
                                        <v-img :src="u.profile_picture || 'https://placehold.co/100'" alt="avatar" />
                                    </v-avatar>
                                    <div class="text-body-2 font-weight-medium">
                                        {{ displayName(u) }}
                                    </div>
                                </div>
                            </td>

                            <!-- Agency Name -->
                            <td>{{ u.agency_name || '—' }}</td>

                            <!-- Email -->
                            <td>{{ u.email || '—' }}</td>

                            <!-- Phone -->
                            <td>{{ u.phone || '—' }}</td>

                            <!-- ITA Number -->
                            <td>{{ u.ita_number || '—' }}</td>

                            <!-- Paid -->
                            <td>
                                <v-chip :color="u.paid_user ? 'green-lighten-4' : 'grey-lighten-3'"
                                    :text-color="u.paid_user ? 'green-darken-4' : 'grey-darken-2'" size="small"
                                    variant="flat">
                                    {{ u.paid_user ? 'Paid' : 'Unpaid' }}
                                </v-chip>
                            </td>

                            <!-- Created -->
                            <td>{{ formatDate(u.created_at) }}</td>

                            <!-- Actions -->
                            <td class="text-right">
                                <v-btn size="small" variant="outlined" color="secondary"
                                    :to="`/dashboard/agents/${u.uid}`">
                                    View
                                </v-btn>
                            </td>
                        </tr>

                        <tr v-if="(agents || []).length === 0">
                            <td colspan="8" class="text-center text-medium-emphasis py-6">
                                No agents found.
                            </td>
                        </tr>
                    </tbody>
                </v-table>


                <!-- Pagination -->
                <v-row class="mt-4" justify="space-between" align="center">
                    <v-col cols="auto">
                        <small class="text-medium-emphasis">
                            Showing {{ pageStart + 1 }}–{{ pageEnd }} of {{ totalCount }}
                        </small>
                    </v-col>
                    <v-col cols="auto">
                        <v-pagination v-model="currentPage" :length="totalPages" :total-visible="5"
                            prev-icon="mdi-chevron-left" next-icon="mdi-chevron-right" @update:modelValue="goToPage" />
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>
    </v-container>
</template>

<script>
import { supabase } from '@/supabaseClient'

export default {
    name: 'AgentsTable',
    data() {
        return {
            agents: [],
            search: '',
            pageSize: 10,
            currentPage: 1,
            totalPages: 1,
            totalCount: 0,
            isLoading: true,
        }
    },
    computed: {
        pageStart() {
            return (this.currentPage - 1) * this.pageSize
        },
        pageEnd() {
            return Math.min(this.pageStart + this.pageSize, this.totalCount || 0)
        },
    },
    methods: {
        displayName(u) {
            const fn = u.first_name?.trim()
            const ln = u.last_name?.trim()
            if (fn || ln) return [fn, ln].filter(Boolean).join(' ')
            return u.email || '—'
        },
        formatDate(d) {
            if (!d) return '—'
            const dt = new Date(d)
            return isNaN(dt) ? '—' : dt.toLocaleDateString()
        },

        async fetchAgents(page = 1) {
            this.isLoading = true
            const from = (page - 1) * this.pageSize
            const to = from + this.pageSize - 1

            try {
                let query = supabase
                    .from('users')
                    .select(`uid, first_name, last_name, email, phone, created_at, "isAdmin", paid_user, profile_picture, is_agent, agency_name, ita_number`, { count: 'exact' })
                    .eq('is_agent', true)

                const s = this.search?.trim()
                if (s) {
                    // OR across name/email/phone
                    // Note: wrap literal %..% inside `.or()` string
                    const esc = s.replace(/[,]/g, '') // avoid breaking the OR clause
                    query = query.or(
                        `first_name.ilike.%${esc}%,last_name.ilike.%${esc}%,email.ilike.%${esc}%,phone.ilike.%${esc}%`
                    )
                }

                const { data, count } = await query
                    .order('created_at', { ascending: false })
                    .range(from, to)
                    .throwOnError()

                this.agents = data || []
                this.totalCount = count ?? this.agents.length
                this.totalPages = Math.max(1, Math.ceil((this.totalCount || 0) / this.pageSize))
            } catch (err) {
                console.error('Error fetching agents:', err)
                this.agents = []
                this.totalCount = 0
                this.totalPages = 1
            } finally {
                this.isLoading = false
            }
        },

        goToPage(page) {
            if (page === this.currentPage) return
            this.currentPage = page
            this.fetchAgents(page)
        },

        applyFilters() {
            this.currentPage = 1
            this.fetchAgents(1)
        },

        clearFilters() {
            this.search = ''
            this.applyFilters()
        },
    },
    async mounted() {
        await this.fetchAgents(this.currentPage)
    },
    async activated() {
        await this.fetchAgents(this.currentPage)
    },
}
</script>

<style scoped>
.v-table thead th {
    font-weight: 600;
}

/* Force identical heights using a single CSS var */
.equal-bar {
    --control-h: 48px;
}

/* matches density="comfortable" */
.equal-bar .v-field {
    --v-input-control-height: var(--control-h);
}

.equal-bar .v-btn {
    height: var(--control-h);
}

/* no inline height props needed */
</style>
